import type { JSX } from "react";

/**
 * Four purpose-built SVG diagrams for the "Git, in plain English" course.
 * Each is self-contained and uses the Nymbl palette via inline values
 * (so the SVG can be embedded anywhere without relying on the parent's
 * css custom properties cascading into <svg>).
 */

const NAVY = "#001847";
const NAVY_SOFT = "#111A45";
const INK = "#1F2A44";
const FG2 = "#4A5568";
const FG3 = "#8894A8";
const MIST = "#F2F5F9";
const CLOUD = "#E6EBF2";
const STONE = "#C8D0DC";
const SKY = "#E8F2FB";
const CYAN = "#2D9BD6";
const BLUE = "#407EC1";
const INDIGO = "#5461AD";
const PURPLE = "#64479D";
const SUCCESS = "#2EA86A";

function DiagramFrame({
  title,
  caption,
  children,
}: {
  title: string;
  caption: string;
  children: JSX.Element;
}) {
  return (
    <div className="gf-card" style={{ overflow: "hidden" }}>
      <div className="gf-card__head">
        <h3>{title}</h3>
        <div className="gf-card__head-meta">Visual</div>
      </div>
      <div
        style={{
          padding: 18,
          background:
            "radial-gradient(120% 100% at 0% 0%, #F7FAFD 0%, #FFFFFF 70%)",
          borderBottom: `1px solid ${CLOUD}`,
        }}
      >
        {children}
      </div>
      <div
        style={{
          padding: "12px 18px",
          fontSize: 12.5,
          color: FG2,
          lineHeight: 1.5,
        }}
      >
        {caption}
      </div>
    </div>
  );
}

// ---------- 1. Version history analogy ----------
export function HistoryDiagram() {
  // A stack of file "snapshots" stepping forward in time, each labeled
  // with a meaningful name. The rightmost is "now".
  const snaps = [
    { t: "proposal.docx", sub: "Apr 2", color: STONE },
    { t: "proposal-v2.docx", sub: "Apr 5", color: STONE },
    { t: "proposal-final.docx", sub: "Apr 8", color: STONE },
    { t: "proposal-FINAL-real.docx", sub: "Apr 9", color: STONE },
  ];

  return (
    <DiagramFrame
      title="The folder you already have"
      caption="On the left, the way most people version files today — by saving copies with new names. On the right, what git does: one history, every version labeled with who and why."
    >
      <svg viewBox="0 0 720 280" width="100%" style={{ display: "block" }}>
        {/* LEFT side — the messy folder */}
        <text
          x={20}
          y={28}
          fontSize={11}
          fontWeight={700}
          fill={FG3}
          letterSpacing={1.4}
        >
          THE OLD WAY
        </text>
        {snaps.map((s, i) => (
          <g key={i} transform={`translate(${20 + i * 14}, ${44 + i * 30})`}>
            <rect
              width={210}
              height={48}
              rx={6}
              fill="#FFFFFF"
              stroke={CLOUD}
            />
            <rect width={6} height={48} rx={0} fill={s.color} />
            <text x={18} y={20} fontSize={12} fontWeight={600} fill={INK}>
              {s.t}
            </text>
            <text x={18} y={36} fontSize={10.5} fill={FG3}>
              {s.sub}
            </text>
          </g>
        ))}
        <text x={20} y={258} fontSize={11} fill={FG3} fontStyle="italic">
          Which one is the latest? Who knows.
        </text>

        {/* Divider */}
        <line
          x1={360}
          y1={20}
          x2={360}
          y2={260}
          stroke={CLOUD}
          strokeDasharray="4 6"
        />

        {/* RIGHT side — the git way */}
        <text
          x={380}
          y={28}
          fontSize={11}
          fontWeight={700}
          fill={CYAN}
          letterSpacing={1.4}
        >
          WITH GIT
        </text>

        {/* The "file" — one document */}
        <rect
          x={380}
          y={44}
          width={320}
          height={56}
          rx={8}
          fill={SKY}
          stroke={CYAN}
        />
        <text x={398} y={68} fontSize={13} fontWeight={700} fill={NAVY}>
          proposal.docx
        </text>
        <text x={398} y={86} fontSize={11} fill={FG2}>
          one file · full history kept underneath
        </text>

        {/* Timeline of versions */}
        <line
          x1={400}
          y1={170}
          x2={680}
          y2={170}
          stroke={STONE}
          strokeWidth={2}
        />
        {[
          { x: 410, label: "v1", who: "Erin", why: "first draft" },
          { x: 480, label: "v2", who: "Sam", why: "fixed pricing table" },
          { x: 550, label: "v3", who: "Erin", why: "added Q3 goals" },
          { x: 620, label: "v4", who: "You", why: "client edits" },
        ].map((p, i) => (
          <g key={i}>
            <circle
              cx={p.x}
              cy={170}
              r={9}
              fill={i === 3 ? CYAN : "#FFFFFF"}
              stroke={i === 3 ? CYAN : INDIGO}
              strokeWidth={2}
            />
            <text
              x={p.x}
              y={195}
              fontSize={10}
              fontWeight={700}
              fill={NAVY}
              textAnchor="middle"
            >
              {p.label}
            </text>
            <text
              x={p.x}
              y={210}
              fontSize={9.5}
              fill={FG3}
              textAnchor="middle"
            >
              {p.who}
            </text>
            <text
              x={p.x}
              y={224}
              fontSize={9.5}
              fill={FG3}
              textAnchor="middle"
            >
              {p.why}
            </text>
          </g>
        ))}

        <text x={380} y={258} fontSize={11} fill={FG3} fontStyle="italic">
          Every version is labeled. Roll back to any of them in seconds.
        </text>
      </svg>
    </DiagramFrame>
  );
}

// ---------- 2. Distributed vs centralized ----------
export function DistributedDiagram() {
  return (
    <DiagramFrame
      title="Why git won: every laptop has the full history"
      caption="Old centralized systems had one source of truth on a server — if it went down, nobody could work. Git gives every collaborator a full copy. You can work on a plane, experiment privately, and only share when you choose to."
    >
      <svg viewBox="0 0 720 320" width="100%" style={{ display: "block" }}>
        {/* LEFT — centralized */}
        <text
          x={20}
          y={26}
          fontSize={11}
          fontWeight={700}
          fill={FG3}
          letterSpacing={1.4}
        >
          CENTRALIZED (THE OLD WAY)
        </text>

        {/* Central server */}
        <g transform="translate(140, 130)">
          <rect
            x={-40}
            y={-30}
            width={80}
            height={60}
            rx={6}
            fill={NAVY}
            stroke={NAVY_SOFT}
          />
          <text
            x={0}
            y={-4}
            fontSize={10.5}
            fontWeight={700}
            fill="#FFFFFF"
            textAnchor="middle"
          >
            SERVER
          </text>
          <text
            x={0}
            y={12}
            fontSize={9}
            fill="rgba(255,255,255,0.7)"
            textAnchor="middle"
          >
            full history
          </text>
        </g>

        {/* Lines to laptops */}
        {[
          { x: 50, y: 60 },
          { x: 230, y: 60 },
          { x: 50, y: 230 },
          { x: 230, y: 230 },
        ].map((p, i) => (
          <line
            key={i}
            x1={140}
            y1={130}
            x2={p.x}
            y2={p.y + 22}
            stroke={STONE}
            strokeWidth={1.5}
            strokeDasharray="3 4"
          />
        ))}

        {/* Laptops — empty (no local history) */}
        {[
          { x: 50, y: 60, n: "Erin" },
          { x: 230, y: 60, n: "Sam" },
          { x: 50, y: 230, n: "Jamie" },
          { x: 230, y: 230, n: "You" },
        ].map((p, i) => (
          <g key={i} transform={`translate(${p.x}, ${p.y})`}>
            <rect width={80} height={44} rx={6} fill="#FFFFFF" stroke={CLOUD} />
            <rect x={6} y={6} width={68} height={32} rx={2} fill={MIST} />
            <text
              x={40}
              y={26}
              fontSize={10}
              fontWeight={600}
              fill={FG3}
              textAnchor="middle"
            >
              {p.n}
            </text>
          </g>
        ))}

        {/* "Server down" banner */}
        <g transform="translate(20, 296)">
          <text
            x={0}
            y={0}
            fontSize={11}
            fill="#C03A2A"
            fontStyle="italic"
            fontWeight={600}
          >
            Server down? Nobody works.
          </text>
        </g>

        {/* Divider */}
        <line
          x1={360}
          y1={20}
          x2={360}
          y2={300}
          stroke={CLOUD}
          strokeDasharray="4 6"
        />

        {/* RIGHT — distributed */}
        <text
          x={380}
          y={26}
          fontSize={11}
          fontWeight={700}
          fill={CYAN}
          letterSpacing={1.4}
        >
          DISTRIBUTED (GIT)
        </text>

        {/* Optional shared GitHub in the center */}
        <g transform="translate(540, 140)">
          <rect
            x={-50}
            y={-32}
            width={100}
            height={64}
            rx={8}
            fill="#FFFFFF"
            stroke={CYAN}
            strokeDasharray="4 4"
          />
          <text
            x={0}
            y={-8}
            fontSize={10}
            fontWeight={700}
            fill={CYAN}
            textAnchor="middle"
            letterSpacing={1.2}
          >
            GITHUB
          </text>
          <text x={0} y={8} fontSize={9.5} fill={FG3} textAnchor="middle">
            shared copy
          </text>
          <text x={0} y={22} fontSize={9} fill={FG3} textAnchor="middle">
            (optional)
          </text>
        </g>

        {/* Laptops each with their own history */}
        {[
          { x: 400, y: 60, n: "Erin", color: CYAN },
          { x: 620, y: 60, n: "Sam", color: BLUE },
          { x: 400, y: 230, n: "Jamie", color: INDIGO },
          { x: 620, y: 230, n: "You", color: PURPLE },
        ].map((p, i) => (
          <g key={i}>
            <line
              x1={540}
              y1={140}
              x2={p.x + 40}
              y2={p.y + 22}
              stroke={STONE}
              strokeWidth={1.5}
            />
            <g transform={`translate(${p.x}, ${p.y})`}>
              <rect
                width={80}
                height={56}
                rx={6}
                fill="#FFFFFF"
                stroke={CLOUD}
              />
              {/* mini-history dots inside */}
              {[0, 1, 2].map((j) => (
                <circle
                  key={j}
                  cx={16 + j * 16}
                  cy={20}
                  r={4}
                  fill={p.color}
                />
              ))}
              <line
                x1={16}
                y1={20}
                x2={48}
                y2={20}
                stroke={p.color}
                strokeWidth={1.5}
              />
              <text
                x={40}
                y={44}
                fontSize={10}
                fontWeight={600}
                fill={INK}
                textAnchor="middle"
              >
                {p.n}
              </text>
            </g>
          </g>
        ))}

        <g transform="translate(380, 296)">
          <text
            x={0}
            y={0}
            fontSize={11}
            fill={SUCCESS}
            fontStyle="italic"
            fontWeight={600}
          >
            GitHub down? Everyone still has the full history locally.
          </text>
        </g>
      </svg>
    </DiagramFrame>
  );
}

// ---------- 3. The three places ----------
export function ThreePlacesDiagram() {
  return (
    <DiagramFrame
      title="The three places your work lives"
      caption="Almost every git command moves work between these three boxes. Learning their names now will pay off for years."
    >
      <svg viewBox="0 0 720 320" width="100%" style={{ display: "block" }}>
        {/* Background grouping: laptop */}
        <rect
          x={20}
          y={40}
          width={440}
          height={250}
          rx={14}
          fill="none"
          stroke={CLOUD}
          strokeDasharray="6 6"
        />
        <text
          x={36}
          y={62}
          fontSize={11}
          fontWeight={700}
          fill={FG3}
          letterSpacing={1.4}
        >
          YOUR LAPTOP
        </text>

        {/* Box 1 — Working tree */}
        <g transform="translate(40, 90)">
          <rect width={180} height={170} rx={10} fill="#FFFFFF" stroke={STONE} />
          <rect width={180} height={32} rx={10} fill={SKY} />
          <rect y={22} width={180} height={10} fill={SKY} />
          <text x={14} y={22} fontSize={12} fontWeight={700} fill={NAVY}>
            Working tree
          </text>
          {/* tiny "files" */}
          {[
            { y: 56, t: "App.jsx", s: "edited" },
            { y: 84, t: "Header.jsx", s: "clean" },
            { y: 112, t: "styles.css", s: "clean" },
            { y: 140, t: "README.md", s: "edited" },
          ].map((f, i) => (
            <g key={i}>
              <rect
                x={14}
                y={f.y}
                width={152}
                height={20}
                rx={4}
                fill={MIST}
              />
              <text x={22} y={f.y + 14} fontSize={11} fill={INK}>
                {f.t}
              </text>
              <text
                x={158}
                y={f.y + 14}
                fontSize={9.5}
                fill={f.s === "edited" ? "#E8A93C" : FG3}
                textAnchor="end"
                fontWeight={600}
              >
                {f.s}
              </text>
            </g>
          ))}
          <text x={14} y={166} fontSize={10} fill={FG3} fontStyle="italic">
            What you see in Finder.
          </text>
        </g>

        {/* Box 2 — Local repo */}
        <g transform="translate(260, 90)">
          <rect width={180} height={170} rx={10} fill="#FFFFFF" stroke={STONE} />
          <rect width={180} height={32} rx={10} fill={SKY} />
          <rect y={22} width={180} height={10} fill={SKY} />
          <text x={14} y={22} fontSize={12} fontWeight={700} fill={NAVY}>
            Local repo (.git)
          </text>

          {/* commit chain */}
          {[0, 1, 2, 3].map((i) => (
            <g key={i}>
              <circle
                cx={36 + i * 36}
                cy={86}
                r={9}
                fill={i === 3 ? CYAN : "#FFFFFF"}
                stroke={INDIGO}
                strokeWidth={2}
              />
              {i > 0 && (
                <line
                  x1={36 + (i - 1) * 36 + 9}
                  y1={86}
                  x2={36 + i * 36 - 9}
                  y2={86}
                  stroke={INDIGO}
                  strokeWidth={1.5}
                />
              )}
            </g>
          ))}
          <text
            x={90}
            y={114}
            fontSize={10}
            fill={FG2}
            textAnchor="middle"
            fontWeight={600}
          >
            history chain
          </text>
          <text x={14} y={146} fontSize={10.5} fill={FG2}>
            Every commit you've made —
          </text>
          <text x={14} y={160} fontSize={10.5} fill={FG2}>
            kept in a hidden{" "}
            <tspan fontFamily="ui-monospace, Menlo" fill={NAVY}>
              .git/
            </tspan>{" "}
            folder.
          </text>
        </g>

        {/* Box 3 — Remote */}
        <g transform="translate(500, 90)">
          <rect width={200} height={170} rx={10} fill={NAVY} />
          <rect width={200} height={32} rx={10} fill={NAVY_SOFT} />
          <rect y={22} width={200} height={10} fill={NAVY_SOFT} />
          <text x={14} y={22} fontSize={12} fontWeight={700} fill="#FFFFFF">
            Remote (GitHub)
          </text>

          {/* cloud shape */}
          <g transform="translate(40, 60)" fill="rgba(255,255,255,0.08)">
            <ellipse cx={60} cy={40} rx={56} ry={26} />
            <circle cx={28} cy={28} r={18} />
            <circle cx={88} cy={26} r={20} />
            <circle cx={60} cy={20} r={22} />
          </g>

          {/* same commits, mirrored */}
          {[0, 1, 2].map((i) => (
            <g key={i}>
              <circle
                cx={60 + i * 30}
                cy={120}
                r={7}
                fill={CYAN}
                opacity={0.95}
              />
              {i > 0 && (
                <line
                  x1={60 + (i - 1) * 30 + 7}
                  y1={120}
                  x2={60 + i * 30 - 7}
                  y2={120}
                  stroke={CYAN}
                  strokeWidth={1.5}
                  opacity={0.6}
                />
              )}
            </g>
          ))}

          <text x={14} y={154} fontSize={10.5} fill="rgba(255,255,255,0.75)">
            The shared copy your
          </text>
          <text x={14} y={166} fontSize={10.5} fill="rgba(255,255,255,0.75)">
            teammates also see.
          </text>
        </g>

        {/* Arrows between boxes */}
        {/* working tree -> local repo */}
        <g>
          <path
            d="M 226 175 L 254 175"
            fill="none"
            stroke={INDIGO}
            strokeWidth={2}
            markerEnd="url(#arrowI)"
          />
          <text
            x={240}
            y={166}
            fontSize={9.5}
            fontWeight={700}
            fill={INDIGO}
            textAnchor="middle"
          >
            commit
          </text>
        </g>
        {/* local repo -> remote */}
        <g>
          <path
            d="M 446 175 L 494 175"
            fill="none"
            stroke={CYAN}
            strokeWidth={2}
            markerEnd="url(#arrowC)"
          />
          <text
            x={470}
            y={166}
            fontSize={9.5}
            fontWeight={700}
            fill={CYAN}
            textAnchor="middle"
          >
            push
          </text>
        </g>
        {/* remote -> local repo (return arrow, slightly below) */}
        <g>
          <path
            d="M 494 200 L 446 200"
            fill="none"
            stroke={PURPLE}
            strokeWidth={2}
            markerEnd="url(#arrowP)"
          />
          <text
            x={470}
            y={216}
            fontSize={9.5}
            fontWeight={700}
            fill={PURPLE}
            textAnchor="middle"
          >
            pull
          </text>
        </g>

        <defs>
          <marker
            id="arrowI"
            viewBox="0 0 10 10"
            refX={8}
            refY={5}
            markerWidth={6}
            markerHeight={6}
            orient="auto"
          >
            <path d="M 0 0 L 10 5 L 0 10 z" fill={INDIGO} />
          </marker>
          <marker
            id="arrowC"
            viewBox="0 0 10 10"
            refX={8}
            refY={5}
            markerWidth={6}
            markerHeight={6}
            orient="auto"
          >
            <path d="M 0 0 L 10 5 L 0 10 z" fill={CYAN} />
          </marker>
          <marker
            id="arrowP"
            viewBox="0 0 10 10"
            refX={8}
            refY={5}
            markerWidth={6}
            markerHeight={6}
            orient="auto"
          >
            <path d="M 0 0 L 10 5 L 0 10 z" fill={PURPLE} />
          </marker>
        </defs>

        <text x={20} y={310} fontSize={11} fill={FG3} fontStyle="italic">
          Commands you'll learn next move work between these three boxes.
        </text>
      </svg>
    </DiagramFrame>
  );
}

// ---------- Registry ----------
export const FOUNDATIONS_DIAGRAMS: Record<string, () => JSX.Element> = {
  history: HistoryDiagram,
  distributed: DistributedDiagram,
  "three-places": ThreePlacesDiagram,
};
